(() => {
  const getNumeric = (value) => {
    const number = Number.parseFloat(value);
    return Number.isFinite(number) ? number : null;
  };

  const readDistribution = (histogram) => {
    const distribution = [];

    histogram
      .querySelectorAll('.jdgm-histogram__row')
      .forEach((row, index) => {
        const rating =
          Number.parseInt(row.getAttribute('data-rating'), 10) ||
          5 - index;

        if (rating < 1 || rating > 5) return;

        const frequencyText =
          row.querySelector('.jdgm-histogram__frequency')?.textContent ||
          row.textContent ||
          '';

        const percentText =
          row.querySelector('.jdgm-histogram__percentage')?.textContent ||
          row.getAttribute('data-percentage') ||
          '';

        const frequencyMatch = frequencyText.match(/\(?(\d+)\)?/);
        const percentMatch = percentText.match(/([\d.]+)\s*%?/);

        distribution.push({
          rating,
          count: frequencyMatch
            ? Number.parseInt(frequencyMatch[1], 10)
            : 0,
          percent: percentMatch
            ? Number.parseFloat(percentMatch[1])
            : null
        });
      });

    distribution.sort((a, b) => b.rating - a.rating);

    const total = distribution.reduce(
      (sum, row) => sum + row.count,
      0
    );

    distribution.forEach((row) => {
      if (row.percent === null) {
        row.percent =
          total > 0
            ? (row.count / total) * 100
            : 0;
      }
    });

    return {
      distribution,
      total
    };
  };

  const readAverageRating = (widget, distributionData) => {
    const candidates = [
      widget.getAttribute('data-average-rating'),
      widget.dataset.averageRating,
      widget
        .querySelector('[data-average-rating]')
        ?.getAttribute('data-average-rating'),
      widget
        .querySelector('[data-score]')
        ?.getAttribute('data-score')
    ];

    for (const candidate of candidates) {
      const rating = getNumeric(candidate);

      if (
        rating !== null &&
        rating >= 0 &&
        rating <= 5
      ) {
        return rating;
      }
    }

    const {
      distribution,
      total
    } = distributionData;

    if (total > 0) {
      const weighted = distribution.reduce(
        (sum, row) => sum + row.rating * row.count,
        0
      );

      return weighted / total;
    }

    return null;
  };

  const buildBars = (distribution) => {
    const bars = document.createElement('div');
    bars.className = 'ts-review-bars';

    [5, 4, 3, 2, 1].forEach((rating) => {
      const data =
        distribution.find((row) => row.rating === rating) ||
        { percent: 0 };

      const row = document.createElement('div');
      row.className = 'ts-review-bars__row';

      const label = document.createElement('span');
      label.className = 'ts-review-bars__label';
      label.textContent = String(rating);

      const track = document.createElement('span');
      track.className = 'ts-review-bars__track';

      const fill = document.createElement('span');
      fill.className = 'ts-review-bars__fill';
      fill.style.width = `${Math.max(
        0,
        Math.min(100, data.percent || 0)
      )}%`;

      const percent = document.createElement('span');
      percent.className = 'ts-review-bars__percent';
      percent.textContent = `${Math.round(data.percent || 0)}%`;

      track.appendChild(fill);
      row.append(label, track, percent);
      bars.appendChild(row);
    });

    return bars;
  };

  const buildTrustArea = (root, leftColumn) => {
    const source = root.querySelector(
      '[data-ts-review-trust-source]'
    );

    if (!source || !leftColumn) return;

    const trustArea = document.createElement('div');
    trustArea.className = 'ts-review-trust';

    /*
      Use only the dedicated Judge.me medals metafield rendered by Liquid.
      This avoids dragging review counters, preview badges, or verified
      counters into the custom card.
    */
    while (source.firstChild) {
      trustArea.appendChild(source.firstChild);
    }

    source.remove();

    if (
      trustArea.children.length ||
      (trustArea.textContent || '').trim()
    ) {
      leftColumn.appendChild(trustArea);
    }
  };

  const hideNativeExtras = (root) => {
    /*
      Hide every native "Write a review" control outside our custom
      heading action, even if Judge.me uses a different wrapper/class.
    */
    root.querySelectorAll('a, button').forEach((element) => {
      if (element.closest('[data-ts-review-actions]')) return;

      const text = (element.textContent || '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

      if (text === 'write a review') {
        element.style.setProperty(
          'display',
          'none',
          'important'
        );

        const wrapper = element.closest(
          '.jdgm-widget-actions-wrapper'
        );

        if (wrapper) {
          wrapper.style.setProperty(
            'display',
            'none',
            'important'
          );
        }
      }
    });

    const selectors = [
      '.jdgm-preview-badge',
      '.jdgm-all-reviews-text',
      '.jdgm-all-reviews-rating-wrapper',
      '.jdgm-verified-count-badget',
      '.jdgm-verified-badge-wrapper'
    ];

    selectors.forEach((selector) => {
      root.querySelectorAll(selector).forEach((element) => {
        if (element.closest('.ts-review-trust')) return;

        element.style.setProperty(
          'display',
          'none',
          'important'
        );
      });
    });
  };

  const setupWriteReview = (root, widget) => {
    const target = root.querySelector(
      '[data-ts-review-actions]'
    );

    const originalLink =
      widget.querySelector('.jdgm-write-rev-link') ||
      Array.from(widget.querySelectorAll('a, button')).find(
        (element) =>
          (element.textContent || '')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase() === 'write a review'
      );

    if (!target || !originalLink) return;

    let proxy = target.querySelector(
      '.ts-review-write-proxy'
    );

    if (!proxy) {
      proxy = document.createElement('button');
      proxy.type = 'button';
      proxy.className =
        'jdgm-write-rev-link ts-review-write-proxy';

      proxy.textContent =
        (originalLink.textContent || 'Write a review').trim();

      proxy.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        originalLink.click();
      });

      target.replaceChildren(proxy);
    }

    originalLink.style.setProperty(
      'display',
      'none',
      'important'
    );

    const wrapper = originalLink.closest(
      '.jdgm-widget-actions-wrapper'
    );

    if (wrapper) {
      wrapper.style.setProperty(
        'display',
        'none',
        'important'
      );
    }
  };

  const enhanceReviews = (root) => {
    if (
      !root ||
      root.dataset.tsReviewsEnhanced === 'true'
    ) {
      return true;
    }

    const widget = root.querySelector('.jdgm-rev-widg');
    const header = widget?.querySelector('.jdgm-rev-widg__header');
    const nativeSummary = header?.querySelector(
      '.jdgm-rev-widg__summary'
    );
    const nativeHistogram = header?.querySelector(
      '.jdgm-histogram'
    );

    if (
      !widget ||
      !header ||
      !nativeSummary ||
      !nativeHistogram
    ) {
      return false;
    }

    const distributionData = readDistribution(
      nativeHistogram
    );

    const averageRating = readAverageRating(
      widget,
      distributionData
    );

    const overview = document.createElement('div');
    overview.className = 'ts-review-overview';

    const left = document.createElement('div');
    left.className = 'ts-review-overview__left';

    const right = document.createElement('div');
    right.className = 'ts-review-overview__right';

    if (averageRating !== null) {
      const rating = document.createElement('div');
      rating.className = 'ts-review-rating';
      rating.textContent = averageRating.toFixed(2);
      left.appendChild(rating);
    }

    const stars = document.createElement('div');
    stars.className = 'ts-review-stars';
    stars.setAttribute('aria-hidden', 'true');
    stars.textContent = '★★★★★';
    left.appendChild(stars);

    if (averageRating !== null) {
      const outOfFive = document.createElement('div');
      outOfFive.className = 'ts-review-rating-copy';
      outOfFive.textContent =
        `${averageRating.toFixed(2)} out of 5`;
      left.appendChild(outOfFive);
    }

    if (distributionData.total > 0) {
      const reviewCount = document.createElement('div');
      reviewCount.className = 'ts-review-count';
      reviewCount.textContent =
        `Based on ${distributionData.total} review${
          distributionData.total === 1 ? '' : 's'
        }`;
      left.appendChild(reviewCount);
    }

    buildTrustArea(root, left);

    right.appendChild(
      buildBars(distributionData.distribution)
    );

    const summarySource = root.querySelector(
      '[data-ts-review-summary-source]'
    );

    if (summarySource) {
      summarySource.hidden = false;
      summarySource.classList.remove(
        'ts-customer-reviews__summary-source'
      );

      /*
        Keep the generated/source line in custom.review_summary.
      */
      if ((summarySource.textContent || '').trim()) {
        right.appendChild(summarySource);
      }
    }

    overview.append(left, right);
    header.prepend(overview);

    setupWriteReview(root, widget);
    hideNativeExtras(root);

    root.dataset.tsReviewsEnhanced = 'true';

    /*
      Judge.me occasionally injects standalone counters/badges after the
      main widget. Clean them up briefly, then disconnect.
    */
    const cleanupObserver = new MutationObserver(() => {
      hideNativeExtras(root);
    });

    cleanupObserver.observe(root, {
      childList: true,
      subtree: true
    });

    window.setTimeout(() => {
      cleanupObserver.disconnect();
      hideNativeExtras(root);
    }, 6000);

    return true;
  };

  const initRoot = (root) => {
    if (
      !root ||
      root.dataset.tsReviewsInit === 'true'
    ) {
      return;
    }

    root.dataset.tsReviewsInit = 'true';

    if (enhanceReviews(root)) return;

    const observer = new MutationObserver(() => {
      if (enhanceReviews(root)) {
        observer.disconnect();
      }
    });

    observer.observe(root, {
      childList: true,
      subtree: true
    });

    window.setTimeout(() => {
      observer.disconnect();
      enhanceReviews(root);
    }, 5000);
  };

  const initAll = () => {
    document
      .querySelectorAll('[data-ts-customer-reviews]')
      .forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initAll,
      { once: true }
    );
  } else {
    initAll();
  }

  document.addEventListener(
    'shopify:section:load',
    initAll
  );
})();